// Tenant settings that every member's browser reads (PR 410, revision 6).
//
//   GET /api/auth/tenant-settings/player-ai-chat-recovery
//       → { enabled: boolean | null }   any active member; null = never set
//   PUT /api/auth/admin/tenant-configuration/player-ai-chat-recovery
//       { enabled: boolean } → { enabled: boolean }   Tenant Admin only
//
// The tenant comes from the session on both routes, never from the path or the
// body. Auth stores the value in `auth.tenants.settings`; the effective value in
// the browser is this one when set, else the deployment's `/config.js`
// `playerAi.pollFallback` ("tenant wins", d33).

import { AdminApiError, request } from "./admin-users-api.js";

export { AdminApiError };

export interface PlayerAiChatRecoverySetting {
  /** Null while no Tenant Admin has set it: the deployment default applies. */
  enabled: boolean | null;
}

/**
 * Tenant-scoped, like every other tenant key in the shell: one mounted tree can
 * outlive a session, and an unscoped key would hand the next tenant this one's
 * setting. The ONE key for the read — the Player AI client's getter and the
 * admin control share it, so a save is seen by both at once.
 */
export function playerAiChatRecoveryQueryKey(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return [
    "auth",
    "tenant-settings",
    "player-ai-chat-recovery",
    tenantId ?? "none",
  ] as const;
}

const UNREADABLE = "The server returned a response we could not read.";

/**
 * One request, and only a boolean (or, on the read, null) as its answer. A 200
 * that is not JSON, or JSON of another shape, is refused rather than read as
 * "off": a proxy page must not quietly change how chat is delivered.
 */
async function readSetting(
  path: string,
  init: RequestInit,
  allowNull: boolean,
): Promise<PlayerAiChatRecoverySetting> {
  let body: unknown;
  try {
    body = await request(path, init);
  } catch (error) {
    if (error instanceof SyntaxError)
      throw new Error(UNREADABLE, { cause: error });
    throw error;
  }
  const enabled =
    typeof body === "object" && body !== null && "enabled" in body
      ? (body as { enabled: unknown }).enabled
      : undefined;
  if (typeof enabled === "boolean" || (allowNull && enabled === null)) {
    return { enabled };
  }
  throw new Error(UNREADABLE);
}

export function getPlayerAiChatRecovery(): Promise<PlayerAiChatRecoverySetting> {
  return readSetting(
    "/api/auth/tenant-settings/player-ai-chat-recovery",
    {},
    true,
  );
}

export async function setPlayerAiChatRecovery(
  enabled: boolean,
): Promise<{ enabled: boolean }> {
  const saved = await readSetting(
    "/api/auth/admin/tenant-configuration/player-ai-chat-recovery",
    { method: "PUT", body: JSON.stringify({ enabled }) },
    false,
  );
  return { enabled: saved.enabled === true };
}
